'use client';

import React, { useEffect, useState } from 'react';
import { Calendar, Clock, Bell } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';

interface Reminder {
  id: string;
  studentName: string;
  action: string;
  dueDate: string;
  priority: 'high' | 'medium' | 'low';
  programme: string;
}

const priorityConfig = {
  high: { dot: 'bg-red-500', label: 'Urgent', text: 'text-red-600' },
  medium: { dot: 'bg-amber-500', label: 'Medium', text: 'text-amber-600' },
  low: { dot: 'bg-blue-400', label: 'Upcoming', text: 'text-blue-600' },
};

export default function RemindersPanel() {
  const [reminders, setReminders] = useState<Reminder[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchReminders() {
      try {
        const supabase = createClient();

        // Students with rejected documents (high priority)
        const { data: rejectedStudents } = await supabase
          .from('document_records')
          .select('submission_id, doc_type, updated_at, student_submissions(programme, overall_status, student_id, user_profiles(first_name, last_name))')
          .eq('status', 'rejected')
          .order('updated_at', { ascending: false })
          .limit(3);

        // Students with incomplete submissions (medium priority)
        const { data: incompleteStudents } = await supabase
          .from('student_submissions')
          .select('id, programme, created_at, overall_status, student_id, user_profiles(first_name, last_name)')
          .eq('overall_status', 'incomplete')
          .order('created_at', { ascending: true })
          .limit(3);

        // Students pending for more than 3 days (low priority)
        const threeDaysAgo = new Date();
        threeDaysAgo.setDate(threeDaysAgo.getDate() - 3);
        const { data: pendingStudents } = await supabase
          .from('student_submissions')
          .select('id, programme, created_at, student_id, user_profiles(first_name, last_name)')
          .eq('overall_status', 'pending')
          .lt('created_at', threeDaysAgo.toISOString())
          .order('created_at', { ascending: true })
          .limit(2);

        const built: Reminder[] = [];
        const seenIds = new Set<string>();

        (rejectedStudents || []).forEach((doc: any) => {
          const sub = Array.isArray(doc.student_submissions) ? doc.student_submissions[0] : doc.student_submissions;
          const profile = sub?.user_profiles ? (Array.isArray(sub.user_profiles) ? sub.user_profiles[0] : sub.user_profiles) : null;
          const name = profile ? `${profile.first_name || ''} ${profile.last_name || ''}`.trim() : 'Student';
          const key = `rej-${sub?.student_id || doc.submission_id}`;
          if (seenIds.has(key)) return;
          seenIds.add(key);
          const dueDate = new Date();
          dueDate.setDate(dueDate.getDate() + 2);
          built.push({
            id: key,
            studentName: name,
            action: `Review rejected ${(doc.doc_type || 'document').replace(/_/g, ' ')}`,
            dueDate: dueDate.toLocaleDateString('en-JM', { day: '2-digit', month: '2-digit', year: 'numeric' }),
            priority: 'high',
            programme: sub?.programme || 'Unknown Programme',
          });
        });

        (incompleteStudents || []).forEach((sub: any) => {
          const profile = sub.user_profiles ? (Array.isArray(sub.user_profiles) ? sub.user_profiles[0] : sub.user_profiles) : null;
          const name = profile ? `${profile.first_name || ''} ${profile.last_name || ''}`.trim() : 'Student';
          const key = `inc-${sub.student_id || sub.id}`;
          if (seenIds.has(key)) return;
          seenIds.add(key);
          const dueDate = new Date();
          dueDate.setDate(dueDate.getDate() + 5);
          built.push({
            id: key,
            studentName: name,
            action: 'Send resubmission reminder',
            dueDate: dueDate.toLocaleDateString('en-JM', { day: '2-digit', month: '2-digit', year: 'numeric' }),
            priority: 'medium',
            programme: sub.programme || 'Unknown Programme',
          });
        });

        (pendingStudents || []).forEach((sub: any) => {
          const profile = sub.user_profiles ? (Array.isArray(sub.user_profiles) ? sub.user_profiles[0] : sub.user_profiles) : null;
          const name = profile ? `${profile.first_name || ''} ${profile.last_name || ''}`.trim() : 'Student';
          const key = `pend-${sub.student_id || sub.id}`;
          if (seenIds.has(key)) return;
          seenIds.add(key);
          const dueDate = new Date();
          dueDate.setDate(dueDate.getDate() + 7);
          built.push({
            id: key,
            studentName: name,
            action: 'Follow up — awaiting document submission',
            dueDate: dueDate.toLocaleDateString('en-JM', { day: '2-digit', month: '2-digit', year: 'numeric' }),
            priority: 'low',
            programme: sub.programme || 'Unknown Programme',
          });
        });

        setReminders(built);
      } catch {
        setReminders([]);
      } finally {
        setLoading(false);
      }
    }
    fetchReminders();
  }, []);

  return (
    <div className="bg-card border border-border rounded-xl shadow-card">
      <div className="flex items-center justify-between px-4 py-3.5 border-b border-border">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-accent/10 flex items-center justify-center">
            <Calendar size={14} className="text-accent" />
          </div>
          <span className="text-sm font-semibold text-foreground">Upcoming Reminders</span>
        </div>
        <span className="text-xs font-semibold text-primary bg-primary/10 px-2 py-0.5 rounded-full">
          {loading ? '…' : reminders.length}
        </span>
      </div>
      {loading ? (
        <div className="px-4 py-8 flex items-center justify-center">
          <div className="w-5 h-5 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
        </div>
      ) : reminders.length === 0 ? (
        <div className="px-4 py-8 flex flex-col items-center justify-center text-center">
          <div className="w-10 h-10 rounded-full bg-green-50 flex items-center justify-center mb-3">
            <Bell size={18} className="text-green-400" />
          </div>
          <p className="text-sm font-medium text-muted-foreground">No upcoming reminders</p>
          <p className="text-xs text-muted-foreground mt-1">All students are up to date</p>
        </div>
      ) : (
        <div className="divide-y divide-border">
          {reminders.map((r) => {
            const p = priorityConfig[r.priority];
            return (
              <div key={r.id} className="px-4 py-3 hover:bg-muted/30 transition-colors">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-start gap-2 min-w-0">
                    <span className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${p.dot}`} />
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-foreground truncate">{r.studentName}</p>
                      <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{r.action}</p>
                      <p className="text-[10px] text-muted-foreground mt-0.5">{r.programme}</p>
                    </div>
                  </div>
                  <div className="flex-shrink-0 text-right">
                    <div className="flex items-center gap-1 justify-end">
                      <Clock size={10} className="text-muted-foreground" />
                      <span className="text-[10px] font-mono text-muted-foreground">{r.dueDate}</span>
                    </div>
                    <span className={`text-[10px] font-semibold ${p.text} mt-0.5 block`}>{p.label}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      <div className="px-4 py-3 border-t border-border">
        <button
          onClick={() => window.location.href = '/admin-dashboard'}
          className="w-full text-xs font-semibold text-primary hover:text-primary/80 transition-colors flex items-center justify-center gap-1.5 py-1"
        >
          <Bell size={12} />
          View all reminders
        </button>
      </div>
    </div>
  );
}